import { MenuItem, Select } from '@material-ui/core';
import React, { ChangeEvent, ReactNode, useState } from 'react';
import { CommunityFilterType, Literals } from 'redux/types/account';
import { Divider, Dropdown, DropdownItemProps } from 'semantic-ui-react';
import styled from 'styled-components';
import BasicPopover from './Popover';
import SelectDropdown from './MuiDropdown';
import { ActionButton, StyledInput } from 'components/styled-components/common';

interface Props {
  defaultValue: number;
  literals: Literals;
  label: string;
  onChange: (_e: ChangeEvent<{ name?: string | undefined; value: unknown }>) => void;
  children: ReactNode;
  withValue?: boolean;
  isEditingTags?: boolean;
}

export const PopupFilterSelect = styled(Select)<{ applyPadding?: boolean }>`
  &&& {
    .MuiSelect-select:focus {
      background: none;
    }
  }
`;

export const StyledFilters = styled(PopupFilterSelect)<{ isFilterSelected?: boolean }>`
  &&& {
    width: auto;
    padding: 1em 1.5em 1em 1em;
    height: 3em;
    border: ${({ isFilterSelected, theme }) =>
      isFilterSelected ? `1px solid ${theme.colors.blue.normal}` : '1px solid rgb(217, 217, 217)'};
    border-radius: 7px;
    color: ${({ theme }) => theme.colors.grey.thirdMedium};
    font-size: 0.8em;
    font-family: Lato, 'Helvetica Neue', Arial, Helvetica, sans-serif;
    & .MuiSelect-icon {
      margin: 0 1em;
      margin-top: 0.1em;
    }
  }
`;

export const FormDropdown = styled(StyledFilters)`
  &&& {
    max-width: unset;
    width: 100%;
    box-sizing: border-box;
    font-size: 0.9em;
    background: #f1f5f9;
    padding: 0 1em;
    border-radius: 0.2em;
    border: 0.125em solid #f1f5f9;
    height: auto;
    max-height: 2.5rem;
    color: #302e37 !important;
    text-align: left;
  }
`;

export const StyledInputBase = styled(StyledInput)`
  font-size: 0.9em;
`;

export const StyledMenuItem = styled(MenuItem)`
  &&& {
    font-family: Lato, 'Helvetica Neue', Arial, Helvetica, sans-serif;
  }
`;

export const PopupFilterDropdown = styled(Dropdown)`
  &&& {
    border: 2px solid ${({ theme }) => theme.colors.grey.light};
    border-radius: 1em;
    font-size: 0.8em;
    min-width: 20em;
    & div.text {
      margin-right: 0;
    }
    & div.menu .item {
      display: flex;
      align-items: center;
    }
    & div.item img {
      margin-top: 0;
      margin-bottom: 0;
    }
    & div.item span {
      font-size: 0.9em;
      font-weight: 600;
      color: ${({ theme }) => theme.colors.black} !important;
      padding: 0 !important;
    }
    & > input.search {
      margin-left: 0 !important;
    }
    & > a.label img {
      display: inline-block;
      margin-right: 0.3em;
    }
  }
`;

const AdvancedSearchContainer = styled.button`
  background: none;
  :hover {
    background: ${({ theme }) => theme.colors.grey.light} !important;
  }
`;

const PopupFilterTypeAndFieldComponent: React.FC<Props> = ({
  defaultValue,
  literals,
  onChange,
  children,
  label,
  withValue,
  isEditingTags = false,
}) => {
  const filterTypeOptions: DropdownItemProps[] = [
    {
      key: CommunityFilterType.IS_ANY_OF,
      text: literals.is_any_of,
      value: CommunityFilterType.IS_ANY_OF,
    },
    {
      key: CommunityFilterType.IS_NONE_OF,
      text: literals.is_none_of,
      value: CommunityFilterType.IS_NONE_OF,
    },
  ];
  const [isAdvancedFilterSelected, setIsAdvancedFilterSelected] = useState(false);

  return (
    <>
      <BasicPopover
        label={label}
        isEditingTags={isEditingTags}
        isFilterSelected={withValue}
        outsideClick={() => setIsAdvancedFilterSelected(false)}
      >
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {!isAdvancedFilterSelected && (
            <ActionButton style={{ paddingTop: 0 }} onClick={() => setIsAdvancedFilterSelected(true)}>
              {literals.advanced_search_label}
            </ActionButton>
          )}
          {isAdvancedFilterSelected && (
            <SelectDropdown
              value={defaultValue}
              onChange={onChange}
              menuItems={filterTypeOptions.map(option => ({
                key: option.key,
                value: option.value as number,
                text: option.text as string,
              }))}
            />
          )}
          {children && (
            <>
              <Divider />
              {React.Children.map(children, child => {
                if (React.isValidElement(child)) {
                  return React.cloneElement(child as any, { isAdvancedFilterSelected });
                }
                return child;
              })}
            </>
          )}
        </div>
      </BasicPopover>
    </>
  );
};

export default PopupFilterTypeAndFieldComponent;
